import * as React from 'react';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State
} from '@progress/kendo-data-query';
import debounce from 'lodash/debounce';

export type GridMode = 'grid' | 'card' | 'cal';

export interface GridToolbarDataStateChangeEvent {
  dataState: State;
}

export interface GridToolbarModeChangeEvent {
  gridMode: GridMode;
}

export interface GridToolBarProps extends State {
  /**
   * Determines grid css class.
   * @default true
   */
  className?: string;
  /**
   * Determines if the search in[ut] is visible on the toolbar.
   * @default true
   */
  showSearch?: boolean;
  /**
  /**
   * The placeholder to display in the search input.
   */
  searchPlaceholder?: string;
  /**
   * Determines if the clear button is visible on the toolbar.
   * @default true
   */
  showClear?: boolean;
  /**
   * Determines if the grid mode toggle button is visible on the toolbar.
   */
  showGridMode?: boolean;
  /**
   * Determines if the card mode toggle button is visible on the toolbar.
   */
  showCardMode?: boolean;
  /**
   * Determines if the calendar mode toggle button is visible on the toolbar.
   */
  showCalendarMode?: boolean;
  /**
   * Determines if the export button is visible on the toolbar.
   * @remarks
   * exportRef must also be set in order for the button to be visible.
   */
  showExport?: boolean;
  /**
   * Determines if the export as PDF button is visible on the toolbar.
   * @remarks
   * exportRef must also be set in order for the button to be visible.
   */
  showPDFExport?: boolean;
  /**
   * Triggered after gridMode change.
   */
  onGridModeChange?: (event: GridToolbarModeChangeEvent) => void;
  /**
   * Fires when the data state of the toolbar is changed.
   */
  onDataStateChange?: (event: GridToolbarDataStateChangeEvent) => void;

  /**
   *
   * @returns Fires when export clicked
   */
  handleExport?: () => void;

  /**
   * Specifies which grid columns should be included in the search filter.
   */
  columnsToSearch?: string[];
  /**
   * @hidden
   */
  children?: React.ReactNode;

  useCustomExportExcel?: boolean;

  isDisablePdfButton?: boolean;

  viewMode?: GridMode;
}

export const GridToolBar = ({
  className = 'grid-toolbar w-full',
  showSearch = true,
  showClear = true,
  showGridMode = true,
  showCardMode = true,
  showCalendarMode = false,
  showExport = true,
  showPDFExport = false,
  handleExport,
  useCustomExportExcel = false,
  isDisablePdfButton = false,
  viewMode = 'grid',
  ...props
}: GridToolBarProps) => {
  const [searchText, setSearchText] = React.useState<string>('');
  const [gridMode, setGridMode] = React.useState<GridMode>(viewMode);

  React.useEffect(() => {
    // alert(gridMode);
  }, [viewMode]);

  /**
   * Only show the mode toggle if grid mode is enabled and at least 1 other mode.
   */
  const canChangeMode = showGridMode && (showCardMode || showCalendarMode);

  /**
   * Uses the searchTerm to construct a composite grid filter, or clears it.
   * @param searchTerm
   * @remarks
   * This function is debounced so if a user types rapidly into the search
   * it won't "smash" the api endpoints.
   * useRef so we don't recreate the function on every re-render.
   */
  const emitFilterChange = React.useRef(
    debounce(async (searchTerm) => {
      if ((searchTerm ?? '') === '') {
        // clear the filter
        raiseDataStateChangeEvent({ filter: { logic: 'or', filters: [] } });
      } else {
        // build a filter from the columnsToSearch
        raiseDataStateChangeEvent({
          filter: { logic: 'or', filters: getFilters(searchTerm) }
        });
      }
    }, 300)
  ).current;

  /**
   * When the component unmounts, the search request could be still in progress.
   * In this case, an error will occur when the searchText state is set.
   * Cancel the debounced function to resolve this problem
   */
  React.useEffect(() => {
    return () => {
      emitFilterChange.cancel();
    };
  }, [emitFilterChange]);

  /**
   * Stores the search term in state then emits the debounced filter change event.
   * @param event - the search input change event
   */
  const handleFilterChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    emitFilterChange(searchTerm);
  };

  /**
   * Clear the Search Term and Filters.
   */
  const handleFilterClear = () => handleFilterChange('');

  /**
   * Changes the current grid mode and raises the grid mode change event.
   * @param mode - the Grid Mode
   */
  const handleGridModeChange = (mode: GridMode) => {
    setGridMode(mode);
    raiseGridModeChangeEvent(mode);
  };

  /**
   * Raise the Grid Mode change event.
   * @param mode - the Grid Mode
   */
  const raiseGridModeChangeEvent = (mode: GridMode) => {
    props.onGridModeChange?.({ gridMode: mode });
  };

  /**
   * Raise the Grid DataState change event. I.e. filters have changed.
   * @param moreData
   */
  const raiseDataStateChangeEvent = (moreData: State) => {
    props.onDataStateChange?.({
      dataState: { ...getDataState(), ...moreData }
    });
  };

  /**
   * Constructs a composite 'contains' filter using the ColumnsToSearch.
   * @param searchTerm
   */
  const getFilters = (searchTerm: string) => {
    const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];
    props.columnsToSearch?.forEach((col) =>
      filters.push({
        field: col,
        operator: 'contains',
        value: `${searchTerm}`
      })
    );
    return filters;
  };

  /**
   * Reassembles the grid data state based on the search term / filters.
   */
  const getDataState = () => {
    return {
      filter: props.filter,
      sort: props.sort,
      skip: props.skip,
      take: props.take !== undefined ? props.take : 20,
      group: props.group
    };
  };

  return (
    <div className={className}>
      <GridToolbar>
        <div className={'d-flex w-100 align-items-center w-full'}>
          {showSearch && (
            <div className={'flex-grow-1 input-group'}>
              <Input
                className={'form-control'}
                value={searchText}
                placeholder={props.searchPlaceholder}
                onChange={(e) => handleFilterChange(e.value)}
              />
              <div className={'input-group-append'}>
                <Button
                  onClick={() => handleFilterChange(searchText)}
                  title={props.searchPlaceholder ?? 'Search'}>
                  <span className={'material-symbols-outlined'}>search</span>
                </Button>
              </div>
            </div>
          )}

          <div
            className={`${
              showSearch
                ? 'd-flex align-items-center justify-content-end'
                : 'd-flex align-items-center w-full justify-content-end'
            }`}>
            {showClear && (
              <Button
                className={'ms-2'}
                title={'Clear filters'}
                onClick={handleFilterClear}>
                <span className={'material-symbols-outlined'}>
                  filter_alt_off
                </span>
              </Button>
            )}

            {canChangeMode && (
              <ButtonGroup className={'ms-2'}>
                {showGridMode && (
                  <Button
                    title={'View grid'}
                    themeColor={gridMode === 'grid' ? 'secondary' : 'base'}
                    onClick={() => handleGridModeChange('grid')}>
                    <span className={'material-symbols-outlined'}>
                      view_list
                    </span>
                  </Button>
                )}
                {showCardMode && (
                  <Button
                    title={'View cards'}
                    themeColor={gridMode === 'card' ? 'secondary' : 'base'}
                    onClick={() => handleGridModeChange('card')}>
                    <span className={'material-symbols-outlined'}>
                      grid_view
                    </span>
                  </Button>
                )}
                {showCalendarMode && (
                  <Button
                    title={'View calendar'}
                    themeColor={gridMode === 'cal' ? 'secondary' : 'base'}
                    onClick={() => handleGridModeChange('cal')}>
                    <span className={'material-symbols-outlined'}>
                      calendar_month
                    </span>
                  </Button>
                )}
              </ButtonGroup>
            )}

            {showExport && !useCustomExportExcel && (
              <Button
                className={'ms-2'}
                onClick={handleExport}
                title={'Download'}>
                <span className={'material-symbols-outlined'}>
                  file_download
                </span>
              </Button>
            )}

            {props.children && <div className={'ms-2'}>{props.children}</div>}
          </div>
        </div>
      </GridToolbar>
    </div>
  );
};
